import {
  createThemeSwiper,
  destroyThemeSwiper,
  bindShopifySectionSwiper,
} from '@theme/swiper';

class ProductSwiper extends HTMLElement {
  connectedCallback() {
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);

    this.cleanupSwiper = bindShopifySectionSwiper(this, () => this.mountSwiper());
  }

  disconnectedCallback() {
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);

    this.cleanupSwiper?.();
    this.cleanupSwiper = undefined;
    this.swiper = undefined;
  }

  mountSwiper() {
    const carousel = this.querySelector('[data-product-swiper]');

    if (!(carousel instanceof HTMLElement)) return undefined;

    destroyThemeSwiper(this.swiper);

    const swiper = createThemeSwiper(carousel, {
      slidesPerView: 'auto',
      spaceBetween: this.getSlideGap(),
      loop: false,
      allowTouchMove: true,
      watchOverflow: true,
      keyboard: {
        enabled: false,
        onlyInViewport: true,
      },
    });

    this.swiper = swiper;
    this.bindPagination(swiper);
    this.syncKeyboardState();

    swiper.on('resize', () => {
      swiper.params.spaceBetween = this.getSlideGap();
      swiper.update();
    });

    return swiper;
  }

  bindPagination(swiper) {
    const dots = [...this.querySelectorAll('[data-product-pagination-dot]')];
    const statusElement = this.querySelector('[data-product-status]');
    const statusTemplate = statusElement?.dataset.statusTemplate ?? '[index] / [length]';

    const update = () => {
      const currentIndex = swiper.activeIndex;
      const currentValue = currentIndex + 1;
      const totalValue = swiper.slides.length;

      dots.forEach((dot, index) => {
        const isActive = index === currentIndex;
        dot.classList.toggle('is-active', isActive);
      });

      if (statusElement) {
        statusElement.textContent = statusTemplate
          .replace('[index]', String(currentValue))
          .replace('[length]', String(totalValue));
      }
    };

    swiper.on('init', update);
    swiper.on('slideChange', update);
    swiper.on('resize', update);
    swiper.on('update', update);

    update();
  }

  getSlideGap() {
    const rawValue = getComputedStyle(this).getPropertyValue('--mg-product-swiper-gap').trim();
    const parsed = Number.parseFloat(rawValue);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  handleFocusIn() {
    this.enableKeyboard();
  }

  handleFocusOut(event) {
    const { relatedTarget } = event;

    if (relatedTarget instanceof Node && this.contains(relatedTarget)) return;

    this.disableKeyboard();
  }

  syncKeyboardState() {
    if (this.contains(document.activeElement)) {
      this.enableKeyboard();
    } else {
      this.disableKeyboard();
    }
  }

  enableKeyboard() {
    this.swiper?.keyboard?.enable?.();
  }

  disableKeyboard() {
    this.swiper?.keyboard?.disable?.();
  }
}

if (!customElements.get('product-swiper')) {
  customElements.define('product-swiper', ProductSwiper);
}
